import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	IconButton,
	Divider
} from '@mui/material';
import axios from 'axios';
import { Delete as DeleteIcon } from '@mui/icons-material';
import i18next from '../lang';
import { rootStore } from '../stores/RootStore';

export interface BulkUserData {
	line: number;
	brandName: string;
	organizationName: string;
	id: string;
	password: string;
	email: string;
	phone: string;
	fax: string;
	postalCode: string;
	prefecture: string;
	city: string;
	streetAddress: string;
	building: string;
	aliasName: string;
}

export interface InvalidBulkUserData {
	user: BulkUserData;
	invalidBrandName: boolean;
	invalidId: boolean;
	invalidPassword: boolean;
	invalidEmail: boolean;
	invalidOrganizationName: boolean;
	invalidPostalCode: boolean;
	invalidPrefecture: boolean;
	invalidCity: boolean;
	invalidStreetAddress: boolean;
	reason: string;
	brandNameReason?: string;
	idReason?: string;
	passwordReason?: string;
	emailReason?: string;
	organizationNameReason?: string;
	postalCodeReason?: string;
	prefectureReason?: string;
	cityReason?: string;
	streetAddressReason?: string;
}

interface UserBulkUploadDialogProps {
	open: boolean;
	onClose: () => void;
	normalUsers: BulkUserData[];
	invalidUsers: InvalidBulkUserData[];
	onSuccess: () => void;
}

const UserBulkUploadDialog: React.FC<UserBulkUploadDialogProps> = ({
	open,
	onClose,
	normalUsers: initialNormalUsers,
	invalidUsers: initialInvalidUsers,
	onSuccess
}) => {
	const { viewStatusStore } = rootStore;
	const [normalUsers, setNormalUsers] = useState<BulkUserData[]>([]);
	const [invalidUsers, setInvalidUsers] = useState<InvalidBulkUserData[]>([]);
	const [editedUsers, setEditedUsers] = useState<Map<number, BulkUserData>>(new Map());

	useEffect(() => {
		setNormalUsers(initialNormalUsers);
		setInvalidUsers(initialInvalidUsers);
		setEditedUsers(new Map());
	}, [initialNormalUsers, initialInvalidUsers]);

	const getEditedUser = (line: number, original: BulkUserData): BulkUserData => {
		return editedUsers.get(line) || original;
	};

	const handleFieldChange = (line: number, original: BulkUserData, field: keyof BulkUserData, value: string) => {
		const current = getEditedUser(line, original);
		const updated = { ...current, [field]: value };
		setEditedUsers(prev => new Map(prev).set(line, updated));
	};

	const handleDeleteInvalidRow = (line: number) => {
		setInvalidUsers(prev => prev.filter(u => u.user.line !== line));
		setEditedUsers(prev => {
			const next = new Map(prev);
			next.delete(line);
			return next;
		});
	};

	const handleDeleteNormalRow = (line: number) => {
		setNormalUsers(prev => prev.filter(u => u.line !== line));
	};

	const handleReverify = async () => {
		// エラー行の編集済みデータを収集
		const usersToVerify: BulkUserData[] = invalidUsers.map(inv => {
			return getEditedUser(inv.user.line, inv.user);
		});
		// 正常行もすべて合わせて再検証
		const allUsers = [...normalUsers, ...usersToVerify];

		try {
			viewStatusStore.setLoadingStatus({ open: true });
			const response = await axios.post('/api/v1/auth/bulk-verify', allUsers);
			setNormalUsers(response.data.normalUsers || []);
			setInvalidUsers(response.data.invalidUsers || []);
			setEditedUsers(new Map());
		} catch (error) {
			console.error('Bulk verify error:', error);
		} finally {
			viewStatusStore.setLoadingStatus({ open: false });
		}
	};

	const handleBulkRegister = async () => {
		try {
			viewStatusStore.setLoadingStatus({ open: true });
			const response = await axios.post('/api/v1/auth/bulk-register', normalUsers);
			const count = response.data.count || normalUsers.length;
			alert(i18next.t('user.bulk_register_success', { count }));
			onSuccess();
			onClose();
		} catch (error) {
			console.error('Bulk register error:', error);
			alert(i18next.t('user.bulk_register_failed'));
		} finally {
			viewStatusStore.setLoadingStatus({ open: false });
		}
	};

	const isRegisterEnabled = invalidUsers.length === 0 && normalUsers.length > 0;

	const getFieldReason = (inv: InvalidBulkUserData, field: keyof BulkUserData): string | undefined => {
		const reasonMap: Record<string, string | undefined> = {
			brandName: inv.brandNameReason,
			id: inv.idReason,
			password: inv.passwordReason,
			email: inv.emailReason,
			organizationName: inv.organizationNameReason,
			postalCode: inv.postalCodeReason,
			prefecture: inv.prefectureReason,
			city: inv.cityReason,
			streetAddress: inv.streetAddressReason,
		};
		return reasonMap[field];
	};

	const renderEditableCell = (
		inv: InvalidBulkUserData,
		field: keyof BulkUserData,
		isInvalid: boolean
	) => {
		const current = getEditedUser(inv.user.line, inv.user);
		if (isInvalid) {
			const reasonKey = getFieldReason(inv, field);
			const helperText = reasonKey ? i18next.t(reasonKey) : '';
			return (
				<TextField
					size="small"
					fullWidth
					error
					value={current[field] || ''}
					onChange={(e) => handleFieldChange(inv.user.line, inv.user, field, e.target.value)}
					helperText={helperText}
					FormHelperTextProps={{ sx: { fontSize: '0.65rem', mx: 0, mt: 0.5 } }}
				/>
			);
		}
		return <>{current[field] || '-'}</>;
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Typography variant="h6" component="div">
					{i18next.t('user.bulk_verify_title')}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
					{/* エラーデータセクション */}
					{invalidUsers.length > 0 && (
						<>
							<Box>
								<Typography variant="h6" gutterBottom color="error">
									{i18next.t('user.bulk_invalid_users')} ({invalidUsers.length})
								</Typography>
								{/* 高さ上限と横スクロールは同じ要素に持たせる。
									分けると横スクロールバーがテーブル全体の最下部に置かれ、
									一番下まで縦スクロールしないと見えなくなる */}
								<TableContainer component={Paper} sx={{ maxHeight: 400 }}>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 40 }}>{i18next.t('common.row')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{i18next.t('user.brand_name')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{i18next.t('user.classroom_name')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('common.id')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('auth.password')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>{i18next.t('contact.email_label')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('address.postal_code')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('address.prefecture')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('address.city')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{i18next.t('address.detail_address')}</TableCell>
												<TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>{i18next.t('common.delete')}</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{invalidUsers.map((inv, index) => (
												<TableRow key={index} sx={{ backgroundColor: 'rgba(211, 47, 47, 0.04)' }}>
													<TableCell>{inv.user.line}</TableCell>
													<TableCell>{renderEditableCell(inv, 'brandName', inv.invalidBrandName)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'organizationName', inv.invalidOrganizationName)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'id', inv.invalidId)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'password', inv.invalidPassword)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'email', inv.invalidEmail)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'postalCode', inv.invalidPostalCode)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'prefecture', inv.invalidPrefecture)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'city', inv.invalidCity)}</TableCell>
													<TableCell>{renderEditableCell(inv, 'streetAddress', inv.invalidStreetAddress)}</TableCell>
													<TableCell>
														<IconButton
															size="small"
															onClick={() => handleDeleteInvalidRow(inv.user.line)}
															color="error"
														>
															<DeleteIcon />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
							<Divider />
						</>
					)}

					{/* 正常データセクション */}
					{normalUsers.length > 0 && (
						<Box>
							<Typography variant="h6" gutterBottom color="success.main">
								{i18next.t('user.bulk_normal_users')} ({normalUsers.length})
							</Typography>
							<TableContainer component={Paper} sx={{ maxHeight: 300 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 40 }}>{i18next.t('common.row')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{i18next.t('user.brand_name')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{i18next.t('user.classroom_name')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('common.id')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>{i18next.t('contact.email_label')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('address.postal_code')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('address.prefecture')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>{i18next.t('address.city')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{i18next.t('address.detail_address')}</TableCell>
											<TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>{i18next.t('common.delete')}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{normalUsers.map((user, index) => (
											<TableRow key={index}>
												<TableCell>{user.line}</TableCell>
												<TableCell>{user.brandName || '-'}</TableCell>
												<TableCell>{user.organizationName}</TableCell>
												<TableCell>{user.id}</TableCell>
												<TableCell>{user.email}</TableCell>
												<TableCell>{user.postalCode}</TableCell>
												<TableCell>{user.prefecture}</TableCell>
												<TableCell>{user.city}</TableCell>
												<TableCell>{user.streetAddress}</TableCell>
												<TableCell>
													<IconButton
														size="small"
														onClick={() => handleDeleteNormalRow(user.line)}
														color="error"
													>
														<DeleteIcon />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="primary">
					{i18next.t('common.cancel')}
				</Button>
				{invalidUsers.length > 0 && (
					<Button onClick={handleReverify} color="primary" variant="outlined">
						{i18next.t('user.bulk_reverify')}
					</Button>
				)}
				<Button
					onClick={handleBulkRegister}
					disabled={!isRegisterEnabled}
					color="primary"
					variant="contained"
				>
					{i18next.t('user.bulk_register')} ({normalUsers.length})
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UserBulkUploadDialog;
